import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { StockChart } from "angular-highcharts";
import * as Highcharts from "highcharts";
import { Subject } from "rxjs";
import { responsiveChart } from "../../../widget-helpers/helpers";
import { multiColorThemes } from "../../common/chartThemes";
// import { ResizedEvent } from 'angular-resize-event';
import { ResizedEvent } from 'angular-resize-event-package';
import { gridItemType, widgetAppearanceType } from "src/app/enums";
import { takeUntil } from "rxjs/operators";
import { I18NextPipe } from "angular-i18next";
import { MatDialog } from "@angular/material/dialog";
import { DashboardService } from "src/app/services/services/dashboard.service";
import { DialogDownloadTableComponent } from "src/app/common/dialogs/dialog-download-table/dialog-download-table.component";

@Component({
    selector: 'app-web-engage-chart',
    templateUrl: './web-engage-chart.component.html',
    styleUrls: ['./web-engage-chart.component.scss'],
    standalone: false
})

export class WebEngageChartComponent implements OnInit, OnDestroy {
  @Input() public gridItem;
  @Input() public data;
  @Input() public columns: any[];
  @Input() public notifier: Subject<any>;
  @Input() public isStandAlone: boolean;
  @Input() public downloadDialogNotifier: Subject<any>;
  @Input() public reportCode: string;
  @Input() public reportInfo: string;
  @Input() public reportTitle: string;


  @ViewChild('chartEl') htmlChart: ElementRef<HTMLDivElement>;

  public hasGrouping: boolean;
  public chart: StockChart;
  public chartProps: any = {
    xAxis: {
      categories: []
    },
    series: []
  };

  private delayRefresh: boolean = false;

  private legendTitle: string;
  private xTitle: string;
  private yTitle: string;

  private ngUnsubscribe = new Subject<void>();

  private legendFontSize: string;
  private legendElementsFontSize: string;
  private xTitleFontSize: string;
  private xLabelsFontSize: string;
  private yTitleFontSize: string;
  private yLabelsFontSize: string;

  private legendColor: string;
  private legendElementsColor: string;
  private xTitleColor: string;
  private xLabelsColor: string;
  private yTitleColor: string;
  private yLabelsColor: string;

  constructor(
    private i18nextPipe: I18NextPipe,
    public dialog: MatDialog,
    private DashboardsService: DashboardService) { }

  ngOnInit(): void {
    this.notifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(ev => {
      setTimeout(() => {
        if (this.data?.length > 0) {
          this.hasGrouping = this.gridItem.setup.sysGroupByTime.trim().length > 0;
          if (this.hasGrouping) {
            this.createChartDataWithGrouping();
          } else {
            this.createChartDataWithoutGrouping();
          }

          this.chart = this.createChartBase();
        }
      }, 0)
    });

    if (this.isStandAlone) {
      this.downloadDialogNotifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        this.downloadChart();
      });
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  downloadChart() {
    const dialogRef = this.dialog.open(DialogDownloadTableComponent, {
      panelClass: 'dialog-download-pannel',
      backdropClass: "backdrop-invisible",
      data: {
        fileName: this.reportCode,
        fileType: "PDF",
        fileTypes: ["PDF", "PNG", "JPEG", "SVG"],
        isChart: true
      },
      width: "495px",
      height: "365px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        let fileName = data.fileName;
        let fileType = data.fileType;
        let width = data.imgWidth;
        let height = data.imgHeight;
        let type: any;
        if (width > 3000) width = 3000;
        if (width < 1900) width = 1900;
        if (height > 1800) height = 1800;
        if (height < 1000) height = 1000;
        switch (fileType) {
          case "PDF": type = "application/pdf"; break;
          case "PNG": type = "image/png"; break;
          case "JPEG": type = "image/jpeg"; break;
          case "SVG": type = "image/svg+xml"; break;
          default: type = "application/pdf"; break;
        }
        let chartOptions = {
          title: {
            text: this.reportInfo
          },
          chart: {
            width: width,
            height: height,

          }
        }
        this.chart.ref.exportChart(
          {
            type: type,
            filename: fileName
          },
          chartOptions
        )
      }
    });
  }

  createChartDataWithGrouping() {
    let seriesKeys = this.columns
      .filter(c => c.id !== "rule_name" && c.id !== "interaction_name")
      .map(c => c.name);
    let categories = [];
    let series = [];

    for (let i = 0; i < seriesKeys.length; i++) {
      series.push({
        name: seriesKeys[i],
        data: []
      })
    }

    for (let i = 0; i < this.data.length; i++) {
      let row = this.data[i];
      //let category = `${row["from_date"]}`;
      let category = new Date(row["from_date"]).toLocaleString('en-UK')

      for (let j = 0; j < series.length; j++) {
        let colId = this.columns.find(c => c.name === series[j].name).id;
        series[j].data.push(row[colId]);
      }

      categories.push(category);
    }

    const chartProps = {
      xAxis: {
        categories: categories
      },
      series
    }

    this.chartProps = chartProps;
  }

  getChartTitleForGrouping() {
    const rule = this.data[0]["rule_name"];
    const interaction = this.data[0]["interaction_name"];

    return `${rule} - ${interaction}`;
  }

  createChartDataWithoutGrouping() {
    let seriesKeys = this.columns
      .filter(c => c.id !== "rule_name" && c.id !== "interaction_name")
      .map(c => c.name);
    let categories = [];
    let series = [];

    for (let i = 0; i < seriesKeys.length; i++) {
      series.push({
        name: seriesKeys[i],
        data: []
      })
    }
    for (let i = 0; i < this.data.length; i++) {
      let row = this.data[i];
      let category = `${row["rule_name"]}<br/>${row["interaction_name"]}`;

      for (let j = 0; j < series.length; j++) {
        let colId = this.columns.find(c => c.name === series[j].name).id;
        series[j].data.push(row[colId]);
      }

      categories.push(category);
    }

    const chartProps = {
      xAxis: {
        categories: categories
      },
      series
    }

    this.chartProps = chartProps;
  }

  private getColorTheme() {
    let colorScheme = this.gridItem.colorScheme;
    if (this.isStandAlone) colorScheme = 0;
    const theme: Highcharts.Options = multiColorThemes[colorScheme !== null ? colorScheme : 0];

    return theme;
  }

  private createChartBase() {
    const { names, fonts, colors } = Object(this.gridItem.setup?.preferences);
    const here = this;
    this.setPreferences();
    const theme = this.getColorTheme();

    let categories = this.chartProps.xAxis.categories;
    let series = this.chartProps.series;

    return new StockChart({
      chart: {
        ...theme.chart,
        events: {
          load: function () {
            Highcharts.addEvent(this.tooltip, 'headerFormatter', function (e: any) {
              if (!e.isFooter) {
                e.text = categories[e.labelConfig.x];
              }
            });
          },
          render: function () {
            const elements = here.htmlChart.nativeElement.querySelectorAll(".highcharts-navigator > *");
            const scrollBars = document.querySelectorAll(".highcharts-scrollbar-thumb");
            scrollBars.forEach(sb => sb.classList.add('gridster-item-content'));
            elements.forEach(e => e.classList.add('gridster-item-content'));
          }
        },
        type: gridItemType[this.gridItem.type]
      },
      yAxis: {
        crosshair: false,
        opposite: false,
        title: {
          text: this.yTitle,
          style: {
            fontSize: this.yTitleFontSize,
            color: this.yTitleColor
          }
        },
        labels: {
          style: {
            fontSize: this.yLabelsFontSize,
            color: this.yLabelsColor
          }
        }
      },
      xAxis: {
        crosshair: false,
        title: {
          text: this.xTitle,
          margin: 10,
          style: {
            fontSize: this.xTitleFontSize,
            color: this.xTitleColor
          }
        },
        labels: {
          formatter: function () {
            return categories[this.value];
          },
          autoRotation: [-90],
          autoRotationLimit: 300,
          style: {
            fontSize: this.xLabelsFontSize,
            color: this.xLabelsColor,
            textOverflow: 'ellipsis'
          }
        }
      },
      series: series,
      legend: {
        enabled: true,
        title: {
          text: this.legendTitle,
          style: {
            fontSize: this.legendFontSize,
            color: this.legendColor
          }
        },
        itemStyle: {
          fontFamily: 'Quicksand',
          fontSize: this.legendElementsFontSize,
          color: this.legendElementsColor
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      rangeSelector: {
        enabled: false
      },
      navigator: {
        xAxis: {
          labels: {
            formatter: function (event) {
              return "";
            }
          }
        }
      },
      plotOptions: {
        series: {
          animation: false,
          marker: {
            enabled: true,
            radius: 4
          }
        }
      },
      tooltip: {
        split: false,
        shared: true,
        pointFormatter: function () {
          let dataIndex = this.x;
          let valueIndex = this.series["index"];
          let category = series[valueIndex];
          let name = category["name"];
          let value = category["data"][dataIndex];
          let serie = here.columns.find(c => name == c.name);
          // let formattedValue = here.formatValue(value, serie?.format);
          let currentSerie: any = this.series;

          return `<br/><span style="color:${currentSerie.color}">${currentSerie.name}</span>: <b>${value}</b>`;
        }
      },
      colors: theme.colors,
      responsive: {
        rules: [
          responsiveChart.height200,
          responsiveChart.height300
        ]
      }
    });
  }

  onResized(event: ResizedEvent) {
    if (this.delayRefresh) return;
    this.delayRefresh = true;
    setTimeout(() => {
      this.delayRefresh = false;
      this.chart = this.createChartBase();
    }, 20);
  }

  private setPreferences() {
    const { names, fonts, colors } = Object(this.gridItem.setup?.preferences);
    this.legendTitle = names && this.getLabelValue(widgetAppearanceType.LeggendTitle, names) || '';
    this.xTitle = names && this.getLabelValue(widgetAppearanceType.XAxisTitle, names) || '';
    this.yTitle = names && this.getLabelValue(widgetAppearanceType.YAxisTitle, names) || '';

    this.legendFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.LeggendTitle, fonts) || '';
    this.legendElementsFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.LeggendElements, fonts) || '';
    this.xTitleFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.XAxisTitle, fonts) || '';
    this.xLabelsFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.XAxisLables, fonts) || '';
    this.yTitleFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.YAxisTitle, fonts) || '';
    this.yLabelsFontSize = fonts && this.getFontSizeValue(widgetAppearanceType.YAxisLables, fonts) || '';

    this.legendColor = colors && this.getColorValue(widgetAppearanceType.LeggendTitle, colors) || this.getDefaultColorValues();
    this.legendElementsColor = colors && this.getColorValue(widgetAppearanceType.LeggendElements, colors) || this.getDefaultColorValues();
    this.xTitleColor = colors && this.getColorValue(widgetAppearanceType.XAxisTitle, colors) || this.getDefaultColorValues();
    this.xLabelsColor = colors && this.getColorValue(widgetAppearanceType.XAxisLables, colors) || this.getDefaultColorValues();
    this.yTitleColor = colors && this.getColorValue(widgetAppearanceType.YAxisTitle, colors) || this.getDefaultColorValues();
    this.yLabelsColor = colors && this.getColorValue(widgetAppearanceType.YAxisLables, colors) || this.getDefaultColorValues();
  }

  private getLabelValue(columnId, names): string {
    return names.filter(column => column.id === columnId)[0]?.name;
  }

  private getFontSizeValue(fontId, fonts): string {
    return fonts.filter(font => font.id === fontId)[0]?.fontSize;
  }

  private getColorValue(colorId, colors): string {
    return colors.filter(color => color.id === colorId)[0]?.color;
  }

  private getDefaultColorValues(): string {
    if (this.gridItem.colorScheme === 3) {
      return "#FFFFFF";
    }

    return "#30424F";
  }

}
